<template>
  <section id="call-section" class="py-24 bg-gradient-to-br from-slate-50 to-white relative overflow-hidden">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid md:grid-cols-2 gap-12 items-center">
        <div class="space-y-8">
          <div>
            <h2 class="text-3xl font-bold text-gray-900 mb-4">{{ $t('callRequest.heading.first') }} <span
                class="text-indigo-600">{{ $t('callRequest.heading.highlighted') }}</span>?</h2>
            <p class="text-xl text-gray-700">
              {{ $t('callRequest.subheading') }}
            </p>
          </div>

          <div class="space-y-6">
            <div class="flex items-start gap-4">
              <div class="rounded-full bg-green-100 p-3 flex-shrink-0">
                <PhoneIcon class="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 class="text-xl font-medium text-gray-900">{{ $t('callRequest.features.personal.title') }}</h3>
                <p class="mt-2 text-gray-600">{{ $t('callRequest.features.personal.description') }}</p>
              </div>
            </div>

            <div class="flex items-start gap-4">
              <div class="rounded-full bg-purple-100 p-3 flex-shrink-0">
                <ClockIcon class="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 class="text-xl font-medium text-gray-900">{{ $t('callRequest.features.quick.title') }}</h3>
                <p class="mt-2 text-gray-600">{{ $t('callRequest.features.quick.description') }}</p>
              </div>
            </div>
          </div>

          <div class="bg-indigo-50 rounded-lg p-6 shadow-md border border-indigo-100">
            <p class="text-lg text-gray-800 italic">
              {{ $t('callRequest.testimonial.quote') }}
            </p>
            <p class="mt-4 font-medium text-gray-900">{{ $t('callRequest.testimonial.author') }}</p>
          </div>
        </div>

        <div class="bg-white rounded-xl shadow-xl p-8 border border-gray-200">
          <form @submit.prevent="handleCallRequest" class="space-y-6">
            <div>
              <label for="fullName" class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t('callRequest.form.fullName') }}
              </label>
              <div class="relative">
                <input id="fullName" v-model="fullName" type="text" required
                  class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-3"
                  :placeholder="$t('callRequest.form.fullNamePlaceholder')" />
              </div>
            </div>

            <div>
              <label for="phone" class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t('callRequest.form.phone') }}
              </label>
              <div class="relative">
                <input id="phone" v-model="phoneNumber" type="tel" required pattern="[0-9]{9}"
                  class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-3"
                  :placeholder="$t('callRequest.form.phonePlaceholder')" />
              </div>
            </div>

            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input id="privacy" v-model="acceptedPrivacy" type="checkbox" required
                  class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              </div>
              <div class="ml-3">
                <label for="privacy" class="text-sm text-gray-600">
                  {{ $t('callRequest.form.privacy.first') }} <router-link to="/privacy-policy" class="text-indigo-600 hover:text-indigo-700">{{ $t('callRequest.form.privacy.link') }}</router-link> {{ $t('callRequest.form.privacy.second') }}
                </label>
              </div>
            </div>

            <button type="submit" :disabled="isSubmitting"
              class="w-full flex justify-center items-center px-5 py-3 border border-indigo-200 text-base font-medium rounded-full text-indigo-600 bg-white hover:bg-indigo-50 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm">
              <span v-if="isSubmitting" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                  </path>
                </svg>
                {{ $t('callRequest.form.submitting') }}
              </span>
              <span v-else>{{ $t('callRequest.form.submit') }}</span>
            </button>

            <Transition enter-active-class="transition ease-out duration-200"
              enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95">
              <div v-if="showSuccess" class="mt-4 p-4 bg-green-50 rounded-md border border-green-200">
                <div class="flex">
                  <CheckCircleIcon class="h-5 w-5 text-green-500" />
                  <div class="ml-3">
                    <p class="text-sm font-medium text-green-800">
                      {{ $t('callRequest.form.successMessage') }}
                    </p>
                  </div>
                </div>
              </div>
            </Transition>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { CheckCircleIcon, PhoneIcon, ClockIcon } from '@heroicons/vue/24/solid'
import { collection, addDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import axios from 'axios'

const { t } = useI18n()
const phoneNumber = ref('')
const fullName = ref('')
const acceptedPrivacy = ref(false)
const isSubmitting = ref(false)
const showSuccess = ref(false)

async function handleCallRequest() {
  if (!phoneNumber.value || !acceptedPrivacy.value || !fullName.value) return

  isSubmitting.value = true

  try {
    // Guardar en Firestore
    await addDoc(collection(db, 'callRequests'), {
      phoneNumber: phoneNumber.value,
      fullName: fullName.value,
      createdAt: new Date(),
      status: 'pending'
    })

    // Enviar email de notificación
    try {
      await axios.post('/api/email/send-call-request', {
        phoneNumber: phoneNumber.value,
        fullName: fullName.value
      });
    } catch (emailError) {
      console.error('Error al enviar notificación de llamada:', emailError);
      // Continuar incluso si falla el envío de correo
    }

    phoneNumber.value = ''
    fullName.value = ''
    acceptedPrivacy.value = false
    showSuccess.value = true

    // Hide success message after 5 seconds
    setTimeout(() => {
      showSuccess.value = false
    }, 5000)
  } catch (error) {
    console.error('Error submitting call request:', error)
  } finally {
    isSubmitting.value = false
  }
}
</script> 